/**
 * HorizontalBarsChart — barre orizzontali colorate ordinate per valore.
 *
 * Replica del componente mobile `src/components/charts/HorizontalBarsChart`:
 *   - una riga per item con label, barra colorata, valore numerico
 *   - la barra ha larghezza proporzionale al rapporto value/max
 *   - i valori negativi NON sono mostrati come barre (out of scope)
 *
 * Implementato in HTML+CSS puro (no SVG) per essere accessibile e
 * responsive senza calcoli viewBox.
 */

import { cn } from '@/lib/utils'

export type HorizontalBarItem = {
  label: string
  value: number
  color: string
}

type Props = {
  items: HorizontalBarItem[]
  unit?: string
  /** Se true, ordina decrescente per `value`. Default true. */
  sort?: boolean
}

export function HorizontalBarsChart({ items, unit = '', sort = true }: Props) {
  const positive = items.filter((it) => it.value > 0)
  if (positive.length === 0) return null

  const sorted = sort ? [...positive].sort((a, b) => b.value - a.value) : positive
  const max = Math.max(...sorted.map((it) => it.value), 1)

  return (
    <ul className="flex flex-col gap-3">
      {sorted.map((it) => {
        const widthPct = Math.max(2, (it.value / max) * 100)
        return (
          <li key={it.label} className="flex items-center gap-3 text-sm">
            <span
              className={cn(
                'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[11px] font-bold text-white',
              )}
              style={{ backgroundColor: it.color }}
              aria-hidden
            >
              {it.label}
            </span>

            <div className="flex flex-1 items-center gap-3">
              <div
                className="h-2.5 rounded-full bg-muted/60"
                style={{ width: '100%' }}
              >
                <div
                  className="h-full rounded-full transition-[width] duration-500"
                  style={{
                    width: `${widthPct}%`,
                    backgroundColor: it.color,
                  }}
                />
              </div>
              <span className="w-24 shrink-0 text-right text-sm font-semibold tabular-nums text-foreground">
                {it.value.toFixed(2).replace('.', ',')} {unit}
              </span>
            </div>
          </li>
        )
      })}
    </ul>
  )
}
